import { z } from "zod";
import { CuidSchema } from "./crm.schemas.js";
import {
  BOOKING_LAYOUTS,
  BOOKING_MODES,
  GUEST_INPUT_KINDS,
  EXTRA_TERM_KINDS,
  MAX_HORIZON_DAYS,
  MAX_NOTICE_HOURS,
  MAX_SLOT_DAYS,
  SLOT_MINUTES,
} from "./booking.js";

/**
 * مخططات صفحات الحجز.
 */

/**
 * الاسم اللطيف في الرابط.
 *
 * حروفٌ لاتينية وأرقام وشرطات فقط: الرابط يُنسخ في رسالة ويُكتب بالهاتف،
 * والعربية فيه تصير `%D8%A7%D8%AC…` — ثمانين حرفًا لا يقرؤها أحد ولا
 * تُملى في مكالمة.
 */
const SlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, "الرابط قصير جدًا.")
  .max(50)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "الرابط بحروف لاتينية وأرقام وشرطات فقط.",
  );

const TenantSlugSchema = z.string().trim().toLowerCase().min(2).max(60);

/* ==========================================================================
   الإدارة
   ========================================================================== */

/** محتوى صفحة العرض — فصولٌ وأرقامٌ وأسئلة. */
const ShowMediaSchema = z
  .object({
    kind: z.enum(["IMAGE", "VIDEO"]).default("IMAGE"),
    url: z.string().trim().min(1).max(1_400_000),
    caption: z.string().trim().max(120).nullable().optional(),
  })
  .nullable()
  .optional();
export const BookingShowSchema = z.object({
  theme: z.enum(["light", "dark"]).default("dark"),
  kicker: z.string().trim().max(80).default(""),
  headline: z.string().trim().max(160).default(""),
  subheadline: z.string().trim().max(400).default(""),
  ctaLabel: z.string().trim().max(40).default("احجز الآن"),
  stats: z
    .array(
      z.object({
        value: z.string().trim().max(24),
        label: z.string().trim().max(60),
      }),
    )
    .max(6)
    .default([]),
  chapters: z
    .array(
      z.object({
        id: z.string().trim().max(40).default(""),
        kicker: z.string().trim().max(80).default(""),
        title: z.string().trim().max(120),
        body: z.string().trim().max(2000).default(""),
        media: ShowMediaSchema,
        bullets: z.array(z.string().trim().max(160)).max(8).default([]),
      }),
    )
    .max(12)
    .default([]),
  faq: z
    .array(
      z.object({
        q: z.string().trim().max(200),
        a: z.string().trim().max(1000),
      }),
    )
    .max(12)
    .default([]),
});

export const CreateBookingPageSchema = z.object({
  /** شكل الصفحة — عادية أو عرض — ومحتوى العرض إن كانت. */
  layout: z.enum(BOOKING_LAYOUTS).default("CLASSIC"),
  show: BookingShowSchema.default({}),
  name: z.string().trim().min(2, "الاسم مطلوب.").max(120),
  slug: SlugSchema,
  description: z.string().trim().max(1000).nullable().optional(),
  calendarId: CuidSchema,
  /** صاحب الوقت — يُسنَد إليه الموعد والنشاط الناتج. */
  ownerId: CuidSchema.nullable().optional(),
  /* قائمة مغلقة: مدةٌ حرة تُنتج شبكةَ أوقاتٍ لا تُقرأ — «١٧ دقيقة». */
  durationMin: z
    .number()
    .int()
    .refine((m) => (SLOT_MINUTES as readonly number[]).includes(m), {
      message: "مدة غير متاحة.",
    })
    .default(30),
  bufferMin: z.number().int().min(0).max(60).default(0),
  /*
   * نافذة ساعات تخصّ الصفحة — "من الساعة كذا إلى كذا".
   *
   * تتقاطع مع ساعات التقويم ولا تتجاوزها: القاعة التي تغلق السادسة لا
   * تفتحها صفحةُ حجز. وترتيبُ الطرفين يُفحص في الخدمة لأن partial()
   * هنا يُسقط أي refine على الكائن.
   */
  windowStart: z
    .string()
    .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, "صيغة الساعة HH:mm.")
    .nullable()
    .optional(),
  windowEnd: z
    .string()
    .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, "صيغة الساعة HH:mm.")
    .nullable()
    .optional(),
  noticeHours: z.number().int().min(0).max(MAX_NOTICE_HOURS).default(4),
  horizonDays: z.number().int().min(1).max(MAX_HORIZON_DAYS).default(30),
  /**
   * أغراض الحجز بأسعارها — يعرّفها صاحب الصفحة ويختار العميل منها.
   *
   * الغرضُ مُنتقًى لا منثور: «تصوير منتج» تُقرأ وتُصنَّف، أما ملاحظةٌ
   * حرّة فتُقرأ ولا تُحصى. ولكلٍّ سعرُه: الجلسةُ ليست كالبودكاست وإن
   * كان المكان واحدًا — فالسعر يتبع ما يُطلب لا الصفحةَ كلَّها. وغرضٌ
   * بلا سعرٍ يأخذ سعر الصفحة، فالتسعير الموحَّد يبقى ممكنًا بلا تكرار.
   */
  purposes: z
    .array(
      z.object({
        name: z.string().trim().min(1, "اسم الغرض مطلوب.").max(80),
        price: z.number().min(0).max(9_999_999).nullable().optional(),
        /** من الكتالوج: خدمةٌ أو باقةٌ ووحدتُها. */
        serviceId: CuidSchema.nullable().optional(),
        packageId: CuidSchema.nullable().optional(),
        unit: z.string().trim().max(40).nullable().optional(),
      }),
    )
    .max(12)
    .default([]),
  /** وسائل الحجز المتاحة — يختار العميل منها ما يناسبه. */
  modes: z
    .array(z.enum(BOOKING_MODES))
    .min(1, "اختر وسيلةً واحدة على الأقل.")
    .default(["ONLINE"]),
  /** مكان الحجز الحضوريّ — عنوانٌ يُقرأ وموقعٌ يُفتح بالملاحة. */
  locationText: z.string().trim().max(200).nullable().optional(),
  locationMapsUrl: z
    .string()
    .trim()
    .url("رابط الموقع غير صالح.")
    .max(500)
    .nullable()
    .optional(),
  /**
   * كيف تُقاس المدّة: ثابتةٌ للموعد، أو مدًى يختاره الحاجز ضمن سقف.
   *
   * المدّة الثابتة تخصّ الحجز المحدَّد — اجتماعٌ نصف ساعة. والمدى يخصّ
   * المورد: استوديو يُحجز ساعةً أو خمسًا، بسقفٍ يمنع احتكار اليوم.
   */
  durationMode: z.enum(["FIXED", "RANGE"]).default("FIXED"),
  maxDurationMin: z.number().int().min(15).max(720).default(240),
  /** عدد الحجوزات المتزامنة على الصفحة. */
  capacity: z.number().int().min(1).max(50).default(1),
  /** نوع الصفحة — يُكتب عند الإنشاء ويُهيّئ النموذج بعدها. */
  kind: z
    .enum(["FREE_CONSULT", "PAID_CONSULT", "PLACE"])
    .default("FREE_CONSULT"),
  /** سياسة الإلغاء والاسترداد — تُقرأ قبل الالتزام. */
  cancellationPolicy: z.string().trim().max(2000).nullable().optional(),
  /**
   * نموذج العميل — مرتّبًا. والخادم يضمن الاسم والبريد فيه مهما أُرسل:
   * بهما يُرسَل التأكيد ويُربط العميل، فلا يُترك حذفُهما للمتصفح.
   */
  guestFields: z
    .array(
      z.object({
        key: z
          .string()
          .trim()
          .min(1)
          .max(40)
          .regex(/^[a-zA-Z0-9_]+$/, "مفتاح الحقل حروفٌ لاتينية."),
        label: z.string().trim().min(1).max(80),
        required: z.boolean().default(false),
        /** نوع إجابة السؤال الحرّ. */
        input: z.enum(GUEST_INPUT_KINDS).optional(),
        /** خيارات الاختيار — للأنواع التي تحتاجها. */
        options: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
      }),
    )
    .max(20)
    .default([]),
  /** شروطٌ إضافية بتكلفتها. */
  extraTerms: z
    .array(
      z.object({
        title: z.string().trim().min(1).max(200),
        kind: z.enum(EXTRA_TERM_KINDS).default("TEXT"),
        value: z.number().min(0).max(99_999_999).nullable().optional(),
      }),
    )
    .max(20)
    .default([]),
  /** سعرٌ مُعلَن — عرضًا لا تحصيلًا، والملاحظة تقول كيف يُدفع. */
  price: z.number().min(0).max(9_999_999).nullable().optional(),
  /** الوحدة التي يُقاس بها السعر بالدقائق — به تُحسب قيمة المدى. */
  priceUnitMin: z.number().int().min(5).max(480).default(60),
  /** نسبة المقدَّم المطلوب — صفرٌ يعني لا مقدَّم. */
  depositPercent: z.number().int().min(0).max(100).default(0),
  /**
   * الشروط والأحكام — تُقرأ قبل الحجز وتُعاد في رسالة التأكيد.
   *
   * كان حدُّها مئةً وعشرين حرفًا حين كانت «ملاحظة سعر» تُكتب في سطر.
   * والشروطُ فقراتٌ: إلغاءٌ وتأخيرٌ وسداد — فحدُّها ألفان.
   */
  priceNote: z.string().trim().max(2000).nullable().optional(),
  /**
   * معرض الصفحة — صورٌ تُرفع وفيديو برابطه.
   *
   * الصورة تُحمل في الصفحة نفسها `data:` (حتى ٩٠٠ كيلوبايت قبل التحويل،
   * أي نحو ١٫٢ مليون حرف بعده) لتظهر لمن يفتح الرابط بلا حسابٍ ولا
   * خادمِ ملفات. أما الفيديو فرابطُ منصّته — قصيرٌ بطبعه. وكان الحدُّ
   * خمسمئة حرفٍ للكلّ، فرُفضت كلُّ صورةٍ مرفوعةٍ عند الإنشاء.
   */
  media: z
    .array(
      z.discriminatedUnion("kind", [
        z.object({
          kind: z.literal("IMAGE"),
          url: z
            .string()
            .trim()
            .max(1_400_000, "الصورة أكبر من المسموح — حتى ٩٠٠ كيلوبايت.")
            .refine(
              (u) => u.startsWith("data:image/") || /^https?:\/\//.test(u),
              "رابط الصورة غير صالح.",
            ),
          caption: z.string().trim().max(120).nullable().optional(),
        }),
        z.object({
          kind: z.literal("VIDEO"),
          url: z.string().trim().url("رابط غير صالح.").max(500),
          caption: z.string().trim().max(120).nullable().optional(),
        }),
      ]),
    )
    .max(12)
    .default([]),
  /** يُنشئ عميلًا محتملًا لمن حجز ولم يُعرف من قبل. */
  createLead: z.boolean().default(true),
  isActive: z.boolean().default(true),
});
export type CreateBookingPageInput = z.infer<typeof CreateBookingPageSchema>;

export const UpdateBookingPageSchema = CreateBookingPageSchema.partial();
export type UpdateBookingPageInput = z.infer<typeof UpdateBookingPageSchema>;

/* ==========================================================================
   العام
   ========================================================================== */

export const PublicPageParamsSchema = z.object({
  tenant: TenantSlugSchema,
  slug: SlugSchema,
});
export type PublicPageParams = z.infer<typeof PublicPageParamsSchema>;

/**
 * طلب الأوقات الشاغرة.
 *
 * المدى محدود: طلبُ سنةٍ كاملة في نداء واحد يمسح تقويمًا كاملًا ويُعيد
 * آلاف الأوقات — على مسارٍ عامّ لا مصادقة عليه.
 */
export const SlotsQuerySchema = z
  .object({
    from: z.coerce.date(),
    to: z.coerce.date(),
  })
  .refine((v) => v.to >= v.from, { message: "نهاية المدى قبل بدايته." })
  .refine(
    (v) => (v.to.getTime() - v.from.getTime()) / 86_400_000 <= MAX_SLOT_DAYS,
    {
      message: `المدى أطول من ${MAX_SLOT_DAYS} يومًا.`,
    },
  );
export type SlotsQuery = z.infer<typeof SlotsQuerySchema>;

/**
 * الحجز.
 *
 * البريد مطلوب: هو ما يُرسَل إليه التأكيد وما يُربط به العميل المحتمل.
 * والوقت يُرسَل ويُعاد التحقّق منه في الخادم — لا يُصدَّق كما ورد.
 */
export const CreateBookingSchema = z.object({
  startAt: z.coerce.date(),
  name: z.string().trim().min(2, "الاسم مطلوب.").max(120),
  email: z.string().trim().toLowerCase().email("البريد غير صحيح.").max(200),
  phone: z.string().trim().max(40).optional(),
  note: z.string().trim().max(1000).optional(),
  /** المدّة المختارة بالدقائق — للمدى فقط، وتُفحص مقابل سقف الصفحة. */
  durationMin: z.number().int().min(5).max(720).optional(),
  /** ما اختاره من أغراض الصفحة ووسائلها — يُفحص مقابلهما في الخادم. */
  purpose: z.string().trim().max(80).optional(),
  mode: z.enum(BOOKING_MODES).optional(),
  /** الموافقة على شروط الصفحة — يرفض الخادم بدونها حجزًا على صفحةٍ لها شروط. */
  agreedTerms: z.boolean().optional(),
  /** إجابات حقول الصفحة الزائدة — بمفاتيحها، ويُبقي الخادم ما تعرفه الصفحة فقط. */
  answers: z
    .record(
      z.union([
        /* نصٌّ قصير — والملفُّ وحده يطول: `data:` لملفٍّ حتى ميغابايت. */
        z.string().trim().max(1_400_000),
        z.object({
          name: z.string().trim().min(1).max(200),
          url: z.string().max(1_400_000),
        }),
      ]),
    )
    .optional(),
});
export type CreateBookingInput = z.infer<typeof CreateBookingSchema>;
